// InQuotationForm.jsx
// -----------------------------------------------------------------------------
// - Renders the Tender details section used in TenderInQuotationPage.
// - Displays DynamicForm (TenderMainForm) in read-only mode with schema-driven
//   metadata when both schema and tenderInfo are available.
// - Adds a Phase selector for Non-Spot tenders and a read-only State field,
//   visually aligned with the same grid style used by DynamicForm.
// - Layout and responsiveness are handled via InQuotationForm.css + DynamicForm.css.
// -----------------------------------------------------------------------------

import React from "react";
import { MInputTextField, MSelect } from "src/M_Components";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import DynamicForm from "../../components/Forms/DynamicForm";
import "./InQuotationForm.css";

/* ============================================================================
   Component
   ============================================================================ */
export default function InQuotationForm({
  tenderInfo, // Tender data object, used both as DynamicForm data and for state display
  setTenderInfo, // Currently not used here (form is read-only in this context)
  phases, // Array of phases (Non-Spot tenders only)
  isSpotTender, // Flag indicating if the tender is Spot or Non-Spot
  selectedPhaseId, // Currently selected phase id (for Non-Spot)
  setSelectedPhaseId, // Setter to update the selected phase
  tenderSchemaOptions, // Schema/metadata for DynamicForm
  tenderFkData, // Not used here; FKs are handled inside DynamicForm
}) {
  const { t } = useTranslation();

  /* -----------------------------------------------------------------------
     Derived data: options for the Phase MSelect (Non-Spot only)
     ----------------------------------------------------------------------- */
  const phaseOptions = (phases || []).map((p) => ({
    label: p.Name,
    value: p.Id,
  }));

  const hasSchema =
    Array.isArray(tenderSchemaOptions) && tenderSchemaOptions.length > 0;
  const hasTender = tenderInfo && Object.keys(tenderInfo || {}).length > 0;

  /* -----------------------------------------------------------------------
     Render
     ----------------------------------------------------------------------- */
  return (
    <div className="inq-form-root">
      {/* -------------------------------------------------------------------
          1) Main DynamicForm block (Tender information, read-only)
          - This will use its own internal grid and responsiveness
            via DynamicForm.css.
         ------------------------------------------------------------------- */}
      <div className="inq-form-dynamic-wrapper">
        {hasSchema && hasTender && (
          <DynamicForm
            key={`tmf-${tenderInfo?.Id ?? "no-id"}`} // remount if tender changes
            mode="view" // read-only in this context
            schema={tenderSchemaOptions} // table options metadata
            data={tenderInfo} // dataset to render
            title={t("Tender Information")}
          />
        )}
      </div>

      {/* -------------------------------------------------------------------
          2) Extra fields block (Phase + State)
          - Uses the **same grid class** as DynamicForm ("alignInputsUserForm")
            so that the fields visually align with the rest of the form.
          - We wrap it with an extra container to control padding/margins and
            responsiveness, without touching DynamicForm itself.
         ------------------------------------------------------------------- */}
      <div className="inq-form-extra-wrapper">
        <div className="inq-form-extra-grid alignInputsUserForm">
          {/* Phase Selector (Non-Spot only) */}
          {!isSpotTender && (
            <div className="inq-form-field">
              <MSelect
                label={t("Phase")}
                autocomplete
                options={phaseOptions}
                value={
                  phaseOptions.find((opt) => opt.value === selectedPhaseId) ||
                  null
                }
                onChangeValue={(selected) => {
                  const newId = selected?.value || null;
                  setSelectedPhaseId(newId);
                }}
              />
            </div>
          )}

          {/* Tender State (always visible, read-only) */}
          <div className="inq-form-field">
            <MInputTextField
              type="text"
              label={t("State")}
              value={tenderInfo?.IsFinished ? t("Finished") : t("InQuotation")}
              disabled
            />
          </div>
        </div>
      </div>
    </div>
  );
}

InQuotationForm.propTypes = {
  tenderInfo: PropTypes.object,
  setTenderInfo: PropTypes.func,
  phases: PropTypes.array,
  isViewMode: PropTypes.bool, // Kept for backward compatibility, not used here
  isSpotTender: PropTypes.bool,
  selectedPhaseId: PropTypes.number,
  setSelectedPhaseId: PropTypes.func,
  tenderSchemaOptions: PropTypes.array,
  tenderFkData: PropTypes.object,
};
